/* eslint-disable react/prop-types */
import { FC, useEffect } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import ManagerPicker from "../ManagerPicker/ManagerPicker";

const selectClassName =
	"flex h-9 w-full items-center rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

interface Props {
	defaultValues: AreaFormValues;
	onSubmit: (values: AreaFormValues) => void;
	isSubmitting: boolean;
	regions: LocationRegion[];
	backPath: string;
	isEdit?: boolean;
	onDelete?: () => void;
	initialManager?: { id: string; name: string } | null;
}

const AreaForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting,
	regions,
	backPath,
	isEdit = false,
	onDelete,
	initialManager
}) => {
	const methods = useForm<AreaFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch } = methods;
	const { errors } = useFormState({ control });

	const nameValue = watch("name");
	const isNew = !defaultValues.name;
	const buttonLabel = isNew || !nameValue ? "Create" : "Save";

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Fields card */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.name ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.name && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6 flex flex-col gap-5">
							<FormField
								control={control}
								name="name"
								rules={{ required: "Name is required" }}
								render={({ field }) => (
									<FormItem>
										<label className="font-heading text-lg font-medium text-foreground mb-4 block">
											Area name
										</label>
										<FormControl>
											<Input
												placeholder="Area name"
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="region"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Region
										</FormLabel>
										<FormControl>
											<select className={selectClassName} {...field}>
												<option value="">None</option>
												{regions.map((region) => (
													<option key={region.id} value={String(region.id)}>
														{region.name}
													</option>
												))}
											</select>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="areaManager"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Area Manager
										</FormLabel>
										<FormControl>
											<ManagerPicker
												onChange={field.onChange}
												initialManager={initialManager}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to={backPath}
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							buttonLabel
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default AreaForm;
